import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { CatalogGrade } from "../models/CatalogGrade";
import { getCatalogGrades } from "../repositories/CatalogGradeRepository";

type Props = {
	label: string;
	value: number | null;
	onChange?: (value: number | null) => void;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	required?: boolean;
	showAll?: boolean;
};

const ALL_GRADE: CatalogGrade = {
	GradeId: -1,
	GradeName: "All",
	IsActive: true,
	CreatedBy: null,
	CreatedDate: null,
	ModifiedBy: null,
	ModifiedDate: null,
};

const useGrades = (onError?: (error: Error) => void) => {
	return useQuery<CatalogGrade[], Error>({
		queryKey: ["catalogGrades"],
		queryFn: () => getCatalogGrades(),
		onError: onError,
	});
};

export const GradeSelector = ({
	label,
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	required = false,
	showAll = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data: grades, isLoading } = useGrades((error) =>
		setError(error.message),
	);

	const options = showAll ? [ALL_GRADE, ...(grades || [])] : grades || [];

	const selectedGrade =
		showAll && value === null
			? ALL_GRADE
			: grades?.find((g) => g.GradeId === value) || null;

	return (
		<>
			<Autocomplete
				id="grade-selector"
				fullWidth
				clearOnEscape
				options={options}
				getOptionLabel={(option) => option.GradeName}
				isOptionEqualToValue={(a, b) => a.GradeId === b.GradeId}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						size="small"
						fullWidth
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedGrade}
				onChange={(_, newValue) => {
					onChange(
						!newValue || newValue.GradeId === -1 ? null : newValue.GradeId,
					);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading grades"
			/>
		</>
	);
};
